/* GENERATED from src/js/ndog-variant-picker.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-variant-picker-v2
  - option1 as a native <select> driving the theme's hidden variant tiles
  - quantity lifted next to the select; price moved down beside the shipping info
  - a custom purchase-type selector (Einmalkauf / Spar-Abo): Seal's own widget is
    hidden and proxy-clicked, so its hidden cart inputs still drive checkout while
    we own the layout (option2 tiles nested under the one-time option) and price.

  Event-driven by design: no permanent shop-wide observers. The only observer is a
  one-shot wait for Seal to inject, which disconnects itself immediately after.

  NOTE: the load-time default (which variant/second-level shows first) is resolved
  server-side in main-product_rd.liquid via ndog_target_variant — tiles, the hidden
  original-selector and the option1 select all render on the same variant, so the
  theme's tile→variant sync has nothing to fight. This component no longer forces
  the default in JS.
*/
if (!customElements.get('ndog-variant-picker')) {
  customElements.define(
    'ndog-variant-picker',
    class NdogVariantPicker extends HTMLElement {
      // Wires up select, quantity, price and the custom purchase-type UI
      connectedCallback() {
        this.select = this.querySelector('[data-ndog-variant-select]');
        this.firstLevel = this.querySelector('.selector-wrapper.first-level');
        this.secondLevel = this.querySelector('.selector-wrapper.second-level');
        this.sealTarget = this.querySelector('.sealsubs-target-element');
        this.origSelect = (this.closest('form') || document).querySelector('select.original-selector');
        this.priceEl = document.querySelector('#price');
        this.purchaseIndex = 0;

        this.onSelectChange = this.onSelectChange.bind(this);
        this.scheduleRender = this.scheduleRender.bind(this);
        this.onTileClick = this.onTileClick.bind(this);

        this.relocatePrice();
        this.bindQuantity();

        if (this.select) this.select.addEventListener('change', this.onSelectChange);
        // option2 tiles change the variant by click — refresh the price then too.
        this.addEventListener('click', this.onTileClick);

        this.renderPrice();
        this.initSubscription();
      }

      // Removes listeners/observers/timers to avoid leaks
      disconnectedCallback() {
        if (this.select) this.select.removeEventListener('change', this.onSelectChange);
        this.removeEventListener('click', this.onTileClick);
        if (this.sealWait) this.sealWait.disconnect();
        if (this.priceGuard) this.priceGuard.disconnect();
        clearTimeout(this.guardTimer);
        clearInterval(this.sealPollTimer);
        if (this.qtyInput) {
          this.qtyInput.removeEventListener('change', this.scheduleRender);
          this.qtyInput.removeEventListener('input', this.scheduleRender);
        }
        if (this.qtyWrap) this.qtyWrap.removeEventListener('click', this.scheduleRender);
      }

      /* ---- layout ---- */

      // Moves the price out of the quantity control into a column next to the shipping info
      relocatePrice() {
        const price = this.querySelector('#price');
        const row = document.querySelector('#product-price-wrapper .row');
        if (!price || !row) return;
        const col = document.createElement('div');
        col.className = 'col-sm-4 ndog-vp__price-col';
        col.appendChild(price);
        row.appendChild(col);
      }

      // Re-renders the price when the quantity changes (stepper or typed input)
      bindQuantity() {
        this.qtyInput = this.querySelector('input[name="quantity"]');
        this.qtyWrap = this.querySelector('.variations-selection--quantity');
        if (this.qtyInput) {
          this.qtyInput.addEventListener('change', this.scheduleRender);
          this.qtyInput.addEventListener('input', this.scheduleRender);
        }
        if (this.qtyWrap) this.qtyWrap.addEventListener('click', this.scheduleRender);
      }

      /* ---- variant select ---- */

      // Clicks the hidden first-level tile matching the chosen option, then refreshes the price
      onSelectChange() {
        const value = this.select.value;
        const tiles = this.firstLevel ? this.firstLevel.querySelectorAll('.one-variation-selection') : [];
        for (const tile of tiles) {
          if (tile.getAttribute('data-value') === value) {
            if (!tile.classList.contains('active')) tile.click();
            break;
          }
        }
        this.scheduleRender();
      }

      // Refreshes the price when a variant tile (e.g. an option2 set) is clicked
      onTileClick(event) {
        if (event.target.closest('.one-variation-selection')) this.scheduleRender();
      }

      /* ---- subscription (custom UI over Seal) ---- */

      // Builds the custom UI once Seal has injected (one-shot wait, then disconnects)
      initSubscription() {
        if (!this.sealTarget) return;
        if (this.sealTarget.querySelector('input.sls-option')) {
          this.buildPurchaseUI();
          return;
        }
        this.sealWait = new MutationObserver(() => {
          if (this.sealTarget.querySelector('input.sls-option')) {
            this.sealWait.disconnect();
            this.sealWait = null;
            this.buildPurchaseUI();
          }
        });
        this.sealWait.observe(this.sealTarget, { childList: true, subtree: true });
        // Safety: stop waiting after a while even if Seal never shows up.
        setTimeout(() => {
          if (this.sealWait) {
            this.sealWait.disconnect();
            this.sealWait = null;
          }
        }, 6000);
      }

      // Renders our own Einmalkauf/Spar-Abo options, hides Seal, nests the tiles under one-time
      buildPurchaseUI() {
        this.sealRadios = Array.from(this.sealTarget.querySelectorAll('input.sls-option'));
        if (this.sealRadios.length < 2) return; // unknown structure — leave Seal's widget visible

        const labelOnce = this.dataset.labelOnetime || 'Einmalkauf';
        const labelSub = this.dataset.labelSubscribe || 'Spar-Abo';
        const ui = document.createElement('div');
        ui.className = 'ndog-vp__purchase';
        ui.innerHTML =
          '<label class="ndog-vp__opt" data-index="0">' +
            '<input type="radio" name="ndog-vp-purchase">' +
            '<span class="ndog-vp__opt-body">' +
              '<span class="ndog-vp__opt-title"></span>' +
            '</span>' +
          '</label>' +
          '<label class="ndog-vp__opt" data-index="1">' +
            '<input type="radio" name="ndog-vp-purchase">' +
            '<span class="ndog-vp__opt-body">' +
              // NOTE: badge must precede title/desc so the floated tag sits top-right and text wraps around it
              '<span class="ndog-vp__badge"></span>' +
              '<span class="ndog-vp__opt-title"></span>' +
              '<span class="ndog-vp__opt-desc"></span>' +
            '</span>' +
            // Decorative per-variant gift image (custom.gift_image), anchored on the box corner
            '<img class="ndog-vp__gift-img" alt="" aria-hidden="true" loading="lazy" hidden>' +
          '</label>';
        const titles = ui.querySelectorAll('.ndog-vp__opt-title');
        titles[0].textContent = labelOnce;
        titles[1].textContent = labelSub;

        // Description under Spar-Abo — resolved per variant, re-rendered on every variant change
        this.descEl = ui.querySelector('.ndog-vp__opt-desc');
        // Badge is rendered live (like price/desc): it shows only when Seal
        // actually reports a discount, and updates as soon as that data exists.
        this.badgeEl = ui.querySelector('.ndog-vp__badge');
        this.giftImgEl = ui.querySelector('.ndog-vp__gift-img');
        this.renderBadge();
        this.renderSubDesc();
        this.renderGiftImage();

        this.sealTarget.parentNode.insertBefore(ui, this.sealTarget);
        this.sealTarget.classList.add('ndog-vp__seal-hidden');

        this.ourRadios = Array.from(ui.querySelectorAll('input[type="radio"]'));
        this.ourRadios.forEach((radio, i) => {
          radio.addEventListener('change', () => this.selectPurchase(i));
        });

        const current = this.sealRadios.findIndex((r) => r.checked);
        this.selectPurchase(current >= 0 ? current : 0, true);
        // Tells ndog-stock-guard the options exist now (greys them out for sold-out variants)
        ui.dispatchEvent(new CustomEvent('ndog:purchase-ready', { bubbles: true }));
      }

      // Selects a purchase type: mirrors our radios, drives Seal, toggles tiles, refreshes price
      selectPurchase(index, silent) {
        this.purchaseIndex = index;
        if (this.ourRadios) this.ourRadios.forEach((r, i) => { r.checked = i === index; });
        if (!silent) {
          const sealRadio = this.sealRadios[index];
          if (sealRadio && !sealRadio.checked) sealRadio.click(); // drives Seal's cart inputs
        }
        this.scheduleRender();
        // Seal writes its cart-property inputs (incl. the discount) with a delay
        // after the proxy click — briefly poll until they exist, then re-render.
        if (!silent) this.waitForSealDiscount();
      }

      // Polls (max 2s) for Seal's async-written discount input, then re-renders
      waitForSealDiscount() {
        clearInterval(this.sealPollTimer);
        let tries = 0;
        this.sealPollTimer = setInterval(() => {
          tries += 1;
          if (this.getDiscountPercent() != null || tries >= 20) {
            clearInterval(this.sealPollTimer);
            this.scheduleRender();
          }
        }, 100);
      }

      // True when a subscription (not the first/one-time option) is selected
      isSubscription() {
        return this.purchaseIndex !== 0;
      }

      // Resolves the Spar-Abo description for the current variant: variant metafield
      // (data-sub-desc on the selected original-selector option) → market-specific
      // section setting (data-sub-desc on the host) → Seal's own text
      getSubDescHtml() {
        let variantDesc = '';
        if (this.origSelect) {
          const option = this.origSelect.options[this.origSelect.selectedIndex];
          if (option) variantDesc = (option.getAttribute('data-sub-desc') || '').trim();
        }
        return (variantDesc || this.dataset.subDesc || this.findSealDescription()).trim();
      }

      // Resolves the mobile-only override for the current variant (custom.abo_text_mobile);
      // returns '' when not maintained — the desktop chain then applies on all viewports
      getSubDescMobileHtml() {
        if (!this.origSelect) return '';
        const option = this.origSelect.options[this.origSelect.selectedIndex];
        return option ? (option.getAttribute('data-sub-desc-mobile') || '').trim() : '';
      }

      // Writes the resolved description under Spar-Abo; hides the line when empty.
      // With a mobile override set, both texts render and CSS picks one per viewport.
      renderSubDesc() {
        if (!this.descEl) return;
        const desktopHtml = this.getSubDescHtml();
        const mobileHtml = this.getSubDescMobileHtml();
        let html = desktopHtml;
        if (mobileHtml && mobileHtml !== desktopHtml) {
          html =
            '<span class="ndog-vp__opt-desc-desktop">' + desktopHtml + '</span>' +
            '<span class="ndog-vp__opt-desc-mobile">' + mobileHtml + '</span>';
        }
        if (html === this.lastDescHtml) return; // only touch the DOM when the text actually changes
        this.descEl.innerHTML = html;
        this.descEl.hidden = !html;
        this.lastDescHtml = html;
      }

      // Resolves the gift-image URL for the current variant (custom.gift_image,
      // via data-sub-image on the selected original-selector option); '' = none
      getGiftImageUrl() {
        if (!this.origSelect) return '';
        const option = this.origSelect.options[this.origSelect.selectedIndex];
        return option ? (option.getAttribute('data-sub-image') || '').trim() : '';
      }

      // Shows the per-variant gift image on the Spar-Abo option; hidden when
      // the current variant has none
      renderGiftImage() {
        if (!this.giftImgEl) return;
        const url = this.getGiftImageUrl();
        if (url === this.lastGiftUrl) return; // only touch the DOM on a real change
        if (url) this.giftImgEl.setAttribute('src', url);
        this.giftImgEl.hidden = !url;
        this.lastGiftUrl = url;
      }

      // Returns Seal's subscription description HTML (.sls-description-content), if present
      findSealDescription() {
        if (!this.sealTarget) return '';
        const subRadio = this.sealRadios && this.sealRadios[1];
        const subContainer = (subRadio && subRadio.closest('.sls-option-container')) || this.sealTarget;
        const source =
          subContainer.querySelector('.sls-description-content') ||
          this.sealTarget.querySelector('.sls-description-content');
        return source ? source.innerHTML.trim() : '';
      }

      // Reads the Spar-Abo discount — Seal's ACTIVE rule is the ONLY source:
      // 1. data-discount-percent (Liquid, from native Shopify selling plans) —
      //    empty while Seal runs in widget mode without selling plans; kicks in
      //    automatically if the store ever switches Seal to native subscriptions.
      // 2. The hidden cart-property input Seal writes into the product form once
      //    the subscription option is active (properties[_sealsub_discount_...]).
      //    Machine-written from the rule itself — unlike the widget's visible
      //    text, which is static marketing copy and may show a stale number.
      // Neither present → null: full price shows, no badge.
      getDiscountPercent() {
        const fromPlan = parseFloat(this.dataset.discountPercent || '');
        if (Number.isFinite(fromPlan) && fromPlan > 0) return fromPlan;
        const form = this.closest('form');
        const input = form && form.querySelector('input[name^="properties[_sealsub_discount_"]');
        if (input) {
          const value = parseFloat(String(input.value).replace(',', '.'));
          if (Number.isFinite(value) && value > 0) return value;
        }
        return null;
      }

      /* ---- price ---- */

      // Renders now and briefly guards #price so any foreign re-write is reverted instantly
      scheduleRender() {
        this.renderPrice();
        this.renderSubDesc();
        this.renderBadge();
        this.renderGiftImage();
        this.watchPriceBriefly();
      }

      // Shows the real Seal discount on the Spar-Abo option once it is known;
      // stays hidden while Seal reports nothing (never a made-up number)
      renderBadge() {
        if (!this.badgeEl) return;
        const discount = this.getDiscountPercent();
        if (discount != null) {
          this.badgeEl.textContent = '-' + discount + '%';
          this.badgeEl.style.display = '';
        } else {
          this.badgeEl.style.display = 'none';
        }
      }

      // Watches #price for ~1.5s after an interaction, reverting any foreign write that deviates
      // from our last render. Several theme scripts (Seal's NaN write, the theme's delayed
      // saleUpdate that strips the decimals to "115", the dot-separated subtotal writer) all
      // target the same node async — re-asserting our value keeps the European format consistent.
      watchPriceBriefly() {
        if (!this.priceEl) return;
        if (!this.priceGuard) {
          this.priceGuard = new MutationObserver(() => {
            if (this.expectedBold == null) return;
            const bold = this.priceEl.querySelector('.price__regular b') || this.priceEl.querySelector('b');
            const strike = this.priceEl.querySelector('.price__regular s');
            const boldNow = bold ? bold.textContent : null;
            const strikeNow = strike ? strike.textContent : null;
            if (boldNow !== this.expectedBold || (this.expectedStrike != null && strikeNow !== this.expectedStrike)) {
              this.renderPrice();
            }
          });
        }
        this.priceGuard.observe(this.priceEl, { childList: true, subtree: true, characterData: true });
        clearTimeout(this.guardTimer);
        this.guardTimer = setTimeout(() => this.priceGuard.disconnect(), 1500);
      }

      // Writes the authoritative price: full, or discounted with struck-through full for subscriptions
      renderPrice() {
        if (!this.priceEl || !this.origSelect) return;
        const option = this.origSelect.options[this.origSelect.selectedIndex];
        const raw = option && option.getAttribute('data-price');
        if (!raw) return;
        const unit = this.parseMoney(raw);
        if (isNaN(unit)) return;
        const base = unit * this.getQuantity();

        const bold = this.priceEl.querySelector('.price__regular b') || this.priceEl.querySelector('b');
        const strike = this.priceEl.querySelector('.price__regular s');
        if (!bold) return;

        let boldText;
        let strikeText;
        // Discount applies only for subscriptions AND only when Seal reports one —
        // otherwise the full price shows with nothing struck through.
        const discount = this.isSubscription() ? this.getDiscountPercent() : null;
        if (discount != null) {
          const sale = base * (1 - discount / 100);
          boldText = this.formatMoney(raw, sale);
          strikeText = this.formatMoney(raw, base);
        } else {
          boldText = this.formatMoney(raw, base);
          strikeText = '';
        }

        // Only touch the DOM when the value actually changes (avoids needless observer churn)
        if (bold.textContent !== boldText) bold.textContent = boldText;
        if (strike && strike.textContent !== strikeText) strike.textContent = strikeText;

        // Remember what we wrote so watchPriceBriefly can revert any foreign re-write
        this.expectedBold = boldText;
        this.expectedStrike = strike ? strikeText : null;
      }

      // Reads the current quantity (defaults to 1)
      getQuantity() {
        const value = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
        return value > 0 ? value : 1;
      }

      // Parses a money string to a number, handling both comma and dot decimal separators
      parseMoney(str) {
        let s = str.replace(/[^0-9.,]/g, '');
        if (s.includes('.') && s.includes(',')) {
          s = s.lastIndexOf(',') > s.lastIndexOf('.')
            ? s.replace(/\./g, '').replace(',', '.')
            : s.replace(/,/g, '');
        } else if (s.includes(',')) {
          s = s.replace(',', '.');
        }
        return parseFloat(s);
      }

      // Formats a number into the same shape as the template (separator + currency text)
      formatMoney(template, value) {
        const usesComma = /,\d{2}(?!\d)/.test(template) || (template.includes(',') && !template.includes('.'));
        let num = value.toFixed(2);
        if (usesComma) num = num.replace('.', ',');
        return template.replace(/[\d.,]+/, num);
      }
    }
  );
}
